import {
  Await,
  Form,
  Outlet,
  useLoaderData,
  useMatches,
  useOutlet,
} from '@remix-run/react';
import type {
  Collection,
  Customer,
  MailingAddress,
  Order,
} from '@shopify/hydrogen/storefront-api-types';
import {Suspense} from 'react';
import {
  Button,
  OrderCard,
  PageHeader,
  Text,
  AccountDetails,
  AccountAddressBook,
  Modal,
  ProductSwimlane,
} from '~/components';
import {FeaturedCollections} from '~/components/FeaturedCollections';
import {
  json,
  defer,
  redirect,
  type LoaderArgs,
  type AppLoadContext,
} from '@shopify/remix-oxygen';
import {flattenConnection} from '@shopify/hydrogen';
import {getFeaturedData} from './featured-products';
import {doLogout} from './account/__private/logout';
import {usePrefixPathWithLocale} from '~/lib/utils';
import {CACHE_NONE, routeHeaders} from '~/data/cache';

//translations
import translation from 'translations/account.json';
import type {routeLocale} from 'domains/global';

// Combining json + Response + defer in a loader breaks the
// types returned by useLoaderData. This is a temporary fix.
type TmpRemixFix = ReturnType<typeof defer<{isAuthenticated: false}>>;

export const headers = routeHeaders;

export async function loader({request, context, params}: LoaderArgs) {
  const {pathname} = new URL(request.url);
  const lang = params.lang;
  const customerAccessToken = await context.session.get('customerAccessToken');
  const isAuthenticated = Boolean(customerAccessToken);
  const loginPath = lang ? `/${lang}/account/login` : '/account/login';
  const isAccountPage = /^\/account\/?$/.test(pathname);

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  if (!isAuthenticated) {
    if (isAccountPage) {
      return redirect(loginPath) as unknown as TmpRemixFix;
    }
    // pass through to public routes
    return json({isAuthenticated: false}) as unknown as TmpRemixFix;
  }

  const customer = await getCustomer(context, customerAccessToken);

  // const heading = customer
  //   ? customer.firstName
  //     ? `Welcome, ${customer.firstName}.`
  //     : `Welcome to your account.`
  //   : 'Account Details';
  const heading = translation[selectedLocale].main.title;

  const orders = flattenConnection(customer.orders) as Order[];

  return defer(
    {
      isAuthenticated,
      customer,
      heading,
      orders,
      addresses: flattenConnection(customer.addresses) as MailingAddress[],
      featuredData: getFeaturedData(context.storefront),
      translation: translation[selectedLocale],
    },
    {
      headers: {
        'Cache-Control': CACHE_NONE,
      },
    },
  );
}

export default function Authenticated() {
  const data = useLoaderData<typeof loader>();
  const outlet = useOutlet();
  const matches = useMatches();

  // routes that export handle { renderInModal: true }
  const renderOutletInModal = matches.some((match) => {
    return match?.handle?.renderInModal;
  });

  // Public routes
  if (!data.isAuthenticated) {
    return <Outlet />;
  }

  // Authenticated routes
  if (outlet) {
    if (renderOutletInModal) {
      return (
        <>
          <Modal cancelLink="/account">
            <Outlet context={{customer: data.customer, translation: data.translation}} />
          </Modal>
          <Account
            {...(data as Account & {translation: typeof data.translation})}
          />
        </>
      );
    } else {
      return <Outlet context={{customer: data.customer}} />;
    }
  }

  return (
    <Account {...(data as Account & {translation: typeof data.translation})} />
  );
}

interface Account {
  customer: Customer;
  orders: Order[];
  heading: string;
  addresses: MailingAddress[];
  featuredData: any; // @todo: help please
}

function Account({
  customer,
  orders,
  heading,
  addresses,
  featuredData,
  translation,
}: Account & {translation: any}) {
  return (
    <>
      <PageHeader heading={heading}>
        <Form method="post" action={usePrefixPathWithLocale('/account/logout')}>
          <button type="submit" className="">
            {translation.main.sign_out_label ?? 'Sign out'}
          </button>
        </Form>
      </PageHeader>
      {orders && (
        <AccountOrderHistory
          orders={orders as Order[]}
          translation={translation}
        />
      )}
      <AccountDetails
        customer={customer as Customer}
        translation={translation}
      />
      <AccountAddressBook
        addresses={addresses as MailingAddress[]}
        customer={customer as Customer}
        translation={translation}
      />
    </>
  );
}

function AccountOrderHistory({
  orders,
  translation,
}: {
  orders: Order[];
  translation: any;
}) {
  return (
    <div className="mt-6">
      <div className="grid w-full gap-4 p-4 py-6 md:gap-8 md:p-8 lg:p-12">
        <h2 className="font-bold text-lead">
          {translation.main.order_history_title ?? 'Order History'}
        </h2>
        {orders?.length ? (
          <Orders orders={orders} translation={translation} />
        ) : (
          <EmptyOrders translation={translation} />
        )}
      </div>
    </div>
  );
}

function EmptyOrders({translation}: {translation: any}) {
  return (
    <div>
      <Text className="mb-1" size="fine" width="narrow" as="p">
        {translation.main.no_order_message ??
          "You haven't placed any orders yet."}
      </Text>
      <div className="w-48">
        <Button
          className="w-full mt-2 text-sm"
          variant="secondary"
          to={usePrefixPathWithLocale('/')}
        >
          {translation.main.start_shopping_label ?? 'Start Shopping'}
        </Button>
      </div>
    </div>
  );
}

function Orders({orders, translation}: {orders: Order[]; translation: any}) {
  return (
    <ul className="grid grid-flow-row grid-cols-1 gap-2 gap-y-6 md:gap-4 lg:gap-6 false sm:grid-cols-3">
      {orders.map((order) => (
        <OrderCard order={order} key={order.id} translation={translation}/>
      ))}
    </ul>
  );
}

const CUSTOMER_QUERY = `#graphql
  query CustomerDetails(
    $customerAccessToken: String!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    customer(customerAccessToken: $customerAccessToken) {
      firstName
      lastName
      phone
      email
      id
      defaultAddress {
        id
        formatted
        firstName
        lastName
        company
        address1
        address2
        country
        province
        city
        zip
        phone
      }
      addresses(first: 6) {
        edges {
          node {
            id
            formatted
            firstName
            lastName
            company
            address1
            address2
            country
            province
            city
            zip
            phone
          }
        }
      }
      orders(first: 250, sortKey: PROCESSED_AT, reverse: true) {
        edges {
          node {
            id
            orderNumber
            processedAt
            financialStatus
            fulfillmentStatus
            currentTotalPrice {
              amount
              currencyCode
            }
            lineItems(first: 2) {
              edges {
                node {
                  variant {
                    image {
                      url
                      altText
                      height
                      width
                    }
                  }
                  title
                }
              }
            }
          }
        }
      }
    }
  }
`;

export async function getCustomer(
  context: AppLoadContext,
  customerAccessToken: string,
) {
  const {storefront} = context;

  const data = await storefront.query<{
    customer: Customer;
  }>(CUSTOMER_QUERY, {
    variables: {
      customerAccessToken,
      country: context.storefront.i18n.country,
      language: context.storefront.i18n.language,
    },
  });

  /**
   * If the customer failed to load, we assume their access token is invalid.
   */
  if (!data || !data.customer) {
    throw await doLogout(context);
  }

  return data.customer;
}
